import { useEffect, useState, type FormEvent } from 'react'
import PublicShell from '../components/PublicShell'
import Spinner from '../components/Spinner'
import CourseCard from '../components/courses/CourseCard'
import { coursesAPI } from '../services/api'
import type { Course } from '../types'

/**
 * Every published course (/learn), most students first, with a search.
 * Public: anyone can look; joining needs an account.
 */
const CourseCatalog = () => {
  const [courses, setCourses] = useState<Course[] | null>(null)
  const [query, setQuery] = useState('')
  const [shown, setShown] = useState('')
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setCourses(null)
    setFailed(false)
    coursesAPI
      .search(shown ? { search: shown, limit: 100 } : { limit: 100 })
      .then(setCourses)
      .catch(() => setFailed(true))
  }, [shown])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setShown(query.trim())
  }

  return (
    <PublicShell>
      <div className="space-y-6" data-testid="course-catalog">
        <header>
          <h1 className="text-3xl font-bold text-gray-50">Courses</h1>
          <p className="mt-1 text-sm text-gray-400">
            Taught by FinCreator&rsquo;s creators: free, or included with a creator&rsquo;s subscription. Most students first.
          </p>
        </header>
        <form onSubmit={submit} className="flex gap-2" role="search">
          <label htmlFor="course-search" className="sr-only">
            Search courses
          </label>
          <input
            id="course-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search, e.g. position sizing"
            className="min-w-0 flex-1 rounded-md border border-terminal-border bg-terminal-surface px-3 py-2 text-sm text-gray-100"
          />
          <button type="submit" className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
            Search
          </button>
        </form>
        {failed ? (
          <p className="text-sm text-gray-400">The courses could not be loaded. Try again in a minute.</p>
        ) : courses === null ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : courses.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="catalog-empty">
            {shown ? `No courses match "${shown}".` : 'No courses yet.'}
          </p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {courses.map((course) => (
              <li key={course.id}>
                <CourseCard course={course} dark />
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-gray-500">Not financial advice. Courses teach; they don&rsquo;t tell you what to buy or sell.</p>
      </div>
    </PublicShell>
  )
}

export default CourseCatalog
